<script setup lang="ts">
  import type { Partner } from '@api/models';
  import { computed, ref } from 'vue';

  import type { CustomerSegmentWithWorkflows, PartnerSummary } from '@stuut/api-sdk/models';
  import { Button } from '@ui/components/button';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { MonetaryText } from '@ui/components/monetary';
  import { QuickView } from '@ui/components/popover';
  import {
    PropertyList,
    PropertyListItem,
    PropertyListItemLabel,
    PropertyListItemValue,
  } from '@ui/components/property-list';

  import { stuut } from '@/api';
  import { UserAvatar } from '@/components/users';
  import { sdk } from '@/sdk';

  const props = defineProps<{
    partner?: Partner;
    partnerId?: string;
    partnerSummary?: PartnerSummary | null;
  }>();

  const fullPartner = ref<Partner>();
  const customerSegment = ref<CustomerSegmentWithWorkflows>();
  const isLoading = ref(false);

  // Compute display name from v1 Partner, v2 PartnerSummary, or fullPartner
  const displayName = computed(() => {
    // v1 Partner with computedName getter
    if (props.partner?.computedName) return props.partner.computedName;
    // v2 PartnerSummary - use fallback chain
    if (props.partnerSummary) {
      return (
        props.partnerSummary.name ||
        props.partnerSummary.legalName ||
        props.partnerSummary.accountNumber ||
        props.partnerSummary.email ||
        props.partnerSummary.id ||
        '--'
      );
    }
    // Fallback to fullPartner (loaded on hover)
    return fullPartner.value?.computedName || '--';
  });

  const loadPartner = async () => {
    if (fullPartner.value || isLoading.value) return; // avoid refetch
    isLoading.value = true;
    const partnerId = props.partnerId || props.partner?.id || props.partnerSummary?.id;
    try {
      const [partner, segment] = await Promise.all([
        stuut.partners.retrieve(partnerId),
        sdk.customerSegments
          .getByPartner({ partnerId: partnerId as string })
          .catch(() => undefined),
      ]);
      fullPartner.value = partner;
      customerSegment.value = segment;
    } finally {
      isLoading.value = false;
    }
  };
</script>

<template>
  <QuickView :disabled="!partner && !partnerId && !partnerSummary" @opened="loadPartner">
    <template #trigger>
      <div
        class="underline decoration-dashed decoration-muted-foreground decoration-1 underline-offset-4 min-w-0 truncate"
        v-bind="$attrs"
      >
        <slot class="text-xs!">
          {{ displayName }}
        </slot>
      </div>
    </template>

    <template #content>
      <div class="flex flex-col gap-4">
        <div v-if="isLoading" class="text-center">
          <LoadingSpinner class="text-xs" />
        </div>

        <template v-else-if="fullPartner">
          <div class="flex items-center gap-4 text-xs truncate">
            <div class="font-semibold truncate">{{ fullPartner.computedName }}</div>
            <UserAvatar :user="fullPartner.assignee ?? undefined" size="sm" />
          </div>

          <PropertyList class="gap-1 text-xs">
            <PropertyListItem>
              <PropertyListItemLabel>Account #</PropertyListItemLabel>
              <PropertyListItemValue>
                {{ fullPartner.account_number ?? fullPartner.remote_id }}
              </PropertyListItemValue>
            </PropertyListItem>

            <PropertyListItem>
              <PropertyListItemLabel>Overdue Balance</PropertyListItemLabel>
              <PropertyListItemValue class="h-4!">
                <MonetaryText
                  :value="fullPartner.invoice_balance ?? 0"
                  :currency="fullPartner.currency"
                />
              </PropertyListItemValue>
            </PropertyListItem>

            <PropertyListItem>
              <PropertyListItemLabel>Open Invoices</PropertyListItemLabel>
              <PropertyListItemValue class="h-4!">
                {{ fullPartner.num_open_invoices ?? 0 }}
              </PropertyListItemValue>
            </PropertyListItem>

            <PropertyListItem v-if="customerSegment">
              <PropertyListItemLabel>Segment</PropertyListItemLabel>
              <PropertyListItemValue>
                <RouterLink
                  class="text-blue"
                  :to="{
                    name: 'playbook.collection.segmentation',
                    query: { segmentation_id: customerSegment.id },
                  }"
                  @click.stop
                >
                  {{ customerSegment.name }}
                </RouterLink>
              </PropertyListItemValue>
            </PropertyListItem>
          </PropertyList>

          <div class="flex items-center justify-between">
            <RouterLink
              :to="{
                name: 'customers.customer',
                params: {
                  id: fullPartner.id,
                },
              }"
              @click.stop
            >
              <Button class="inline-block truncate text-left" variant="link" size="inline">
                View Details
                <i class="far fa-arrow-right-long ml-1" />
              </Button>
            </RouterLink>
          </div>
        </template>
      </div>
    </template>
  </QuickView>
</template>

<style scoped></style>
